// Rule: Use Hotwire (Turbo and Stimulus) for dynamic, SPA-like interactions
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["startDate", "endDate", "startInput", "endInput", "selectedRange"]
  static values = {
    unavailableDates: Array,
    minStay: { type: Number, default: 2 },
    minDays: { type: Number, default: 1 },
    maxDays: { type: Number, default: 14 },
    weekendMinimumStay: { type: Number, default: 2 },
    holidayMinimumStay: { type: Number, default: 3 },
    allowPastDates: { type: Boolean, default: false }
  }

  async connect() {
    this.isProcessingDateChange = false

    this.initializeDates()
    await this.initDatePickers()
    this.setupDateChangeListeners()
    this.setupAdminOverrideListener()

    // Ensure dates are synchronized before form submission
    if (this.element.tagName === 'FORM') {
      this.element.addEventListener('submit', (event) => {
        this.beforeSubmit(event)
      })
    }

    // Pathway 2: outside-click dismissal — hide both popovers when the user
    // clicks anywhere outside this controller's element. Bound here so we can
    // remove the exact same reference in disconnect().
    this._outsideClickHandler = (event) => {
      // The flowbite popover renders on <body>, outside this controller's
      // element, so a mousedown on a calendar cell is not contained here.
      // Treat clicks inside a .datepicker popover as inside — otherwise we
      // hide the picker on mousedown before the click can select the date.
      if (this.element.contains(event.target)) return
      if (event.target.closest?.(".datepicker")) return
      this.startDatepicker?.hide()
      this.endDatepicker?.hide()
    }
    document.addEventListener('mousedown', this._outsideClickHandler)

    // Listen for URL parameter changes
    window.addEventListener('popstate', this.handlePopState.bind(this))

    // Initial sync from URL parameters
    this.syncFromUrlParams()

    // Update the selected range display
    this.updateSelectedRangeDisplay()
  }

  disconnect() {
    window.removeEventListener('popstate', this.handlePopState.bind(this))
    if (this._outsideClickHandler) {
      document.removeEventListener('mousedown', this._outsideClickHandler)
    }

    // Clean up date pickers when controller disconnects
    if (this.startDatepicker) {
      this.startDatepicker.destroy()
    }

    if (this.endDatepicker) {
      this.endDatepicker.destroy()
    }

    if (this.dateRangePicker) {
      this.dateRangePicker.destroy()
    }
  }
  
  handlePopState() {
    this.syncFromUrlParams()
  }
  
  syncFromUrlParams() {
    // Get URL parameters
    const urlParams = new URLSearchParams(window.location.search)
    const startDateParam = urlParams.get('start_date')
    const endDateParam = urlParams.get('end_date')
    
    // Update input values if URL parameters exist
    if (startDateParam && this.hasStartInputTarget) {
      try {
        const startDate = this.parseDate(startDateParam)
        if (startDate) {
          this.updateDateInput(this.startInputTarget, this.formatDisplayDate(startDate), this.formatDate(startDate))
          if (this.startDatepicker) {
            this.startDatepicker.setDate(startDate)
          }
        }
      } catch (e) {
        console.error('Error parsing start date from URL:', e)
      }
    }
    
    if (endDateParam && this.hasEndInputTarget) {
      try {
        const endDate = this.parseDate(endDateParam)
        if (endDate) {
          this.updateDateInput(this.endInputTarget, this.formatDisplayDate(endDate), this.formatDate(endDate))
          if (this.endDatepicker) {
            this.endDatepicker.setDate(endDate)
          }
        }
      } catch (e) {
        console.error('Error parsing end date from URL:', e)
      }
    }
    
    // Update the selected range display
    this.updateSelectedRangeDisplay()
  }
  
  initializeDates() {
    // Only initialize dates if they're not already set and no placeholder exists
    if (this.hasStartInputTarget && !this.startInputTarget.value && !this.startInputTarget.placeholder) {
      // Set start date to today by default
      const today = new Date()
      this.updateDateInput(this.startInputTarget, this.formatDisplayDate(today), this.formatDate(today))
    }
    
    if (this.hasEndInputTarget && !this.endInputTarget.value && !this.endInputTarget.placeholder) {
      // Set end date to tomorrow by default
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + this.minStayValue)
      this.updateDateInput(this.endInputTarget, this.formatDisplayDate(tomorrow), this.formatDate(tomorrow))
    }
  }
  
  setupDateChangeListeners() {
    // Listen for changes to start date
    if (this.hasStartInputTarget) {
      this.startInputTarget.addEventListener('change', this.handleStartDateChange.bind(this))
      
      // Handle focus event to clear placeholder
      this.startInputTarget.addEventListener('focus', (event) => {
        // If the input has a placeholder and no value, show the datepicker
        if (event.target.placeholder && !event.target.value) {
          // Clear the placeholder temporarily to show the datepicker
          const originalPlaceholder = event.target.placeholder
          event.target.placeholder = ''
          
          // Restore placeholder on blur if no value was selected
          event.target.addEventListener('blur', () => {
            if (!event.target.value) {
              event.target.placeholder = originalPlaceholder
            }
          }, { once: true })
        }
      })
    }
    
    // Listen for changes to end date
    if (this.hasEndInputTarget) {
      this.endInputTarget.addEventListener('change', this.handleEndDateChange.bind(this))
      
      // Handle focus event to clear placeholder
      this.endInputTarget.addEventListener('focus', (event) => {
        // If the input has a placeholder and no value, show the datepicker
        if (event.target.placeholder && !event.target.value) {
          // Clear the placeholder temporarily to show the datepicker
          const originalPlaceholder = event.target.placeholder
          event.target.placeholder = ''
          
          // Restore placeholder on blur if no value was selected
          event.target.addEventListener('blur', () => {
            if (!event.target.value) {
              event.target.placeholder = originalPlaceholder
            }
          }, { once: true })
        }
      })
    }
  }
  
  setupAdminOverrideListener() {
    const adminOverrideCheckbox = document.querySelector('#reservation_override_minimum_stay')
    if (adminOverrideCheckbox) {
      adminOverrideCheckbox.addEventListener('change', async () => {
        if (this.hasStartInputTarget && this.startInputTarget.value) {
          await this.handleStartDateChange(new Event('change'))
        }
      })
    }
  }

  async handleStartDateChange(event) {
    console.log('DIRECT TEST: handleStartDateChange called', event)
    
    // Check if this event was triggered by the date adjuster
    if (event.detail && event.detail.source === 'dateAdjuster' && event.detail.preventRecursion) {
      return
    }
    
    // Parse the start date
    const startDate = this.parseDate(this.startInputTarget.value)
    if (!startDate) {
      console.error('Invalid start date')
      return
    }
    
    console.log('Start date changed:', this.formatDate(startDate), 'Day of week:', startDate.getDay())
    
    // Update the start date input with formatted value for display
    this.updateDateInput(this.startInputTarget, this.formatDisplayDate(startDate), this.formatDate(startDate))
    
    // Dispatch a custom event for minimum stay validation
    document.dispatchEvent(new CustomEvent('date-selected', {
      detail: { date: startDate, type: 'start' }
    }))
    
    // Update the selected range display
    this.updateSelectedRangeDisplay()
    
    // Check if admin override is enabled
    const adminOverrideCheckbox = document.querySelector('#reservation_override_minimum_stay')
    const isAdminOverrideEnabled = adminOverrideCheckbox && adminOverrideCheckbox.checked
    
    console.log('🔍 ADMIN OVERRIDE DEBUG:')
    console.log('  - Checkbox found:', !!adminOverrideCheckbox)
    console.log('  - Checkbox checked:', adminOverrideCheckbox ? adminOverrideCheckbox.checked : 'N/A')
    console.log('  - Override enabled:', isAdminOverrideEnabled)
    
    // Update end date minimum and adjust end date if needed
    if (this.hasEndInputTarget && this.endDatepicker) {
      // Check for weekend days (Friday = 5, Saturday = 6 in JavaScript)
      const isFriday = startDate.getDay() === 5
      const isSaturday = startDate.getDay() === 6
      
      if (isFriday && !isAdminOverrideEnabled) {
        console.log('*** FRIDAY DETECTED! Setting checkout to Sunday ***')
        
        // For Friday, always set checkout to Sunday (2 days later)
        const sundayCheckout = new Date(startDate)
        sundayCheckout.setDate(startDate.getDate() + 2)
        
        // Update the end date input and datepicker
        this.updateDateInput(this.endInputTarget, this.formatDisplayDate(sundayCheckout), this.formatDate(sundayCheckout))
        this.endDatepicker.setDate(sundayCheckout)
        
        // Set the minimum date for the end datepicker
        this.endDatepicker.setOptions({
          minDate: sundayCheckout
        })
        
        // Dispatch a custom event for the end date change
        document.dispatchEvent(new CustomEvent('date-selected', {
          detail: { date: sundayCheckout, type: 'end' }
        }))
        
        // Update the selected range display
        this.updateSelectedRangeDisplay()
        return
      } else if (isSaturday && !isAdminOverrideEnabled) {
        console.log('*** SATURDAY DETECTED! Setting minimum stay to 2 nights ***')
        
        // For Saturday, enforce 2-night minimum (checkout on Monday)
        const mondayCheckout = new Date(startDate)
        mondayCheckout.setDate(startDate.getDate() + 2)
        
        // Update the end date input and datepicker
        this.updateDateInput(this.endInputTarget, this.formatDisplayDate(mondayCheckout), this.formatDate(mondayCheckout))
        this.endDatepicker.setDate(mondayCheckout)
        
        // Set the minimum date for the end datepicker
        this.endDatepicker.setOptions({
          minDate: mondayCheckout
        })
        
        // Dispatch a custom event for the end date change
        document.dispatchEvent(new CustomEvent('date-selected', {
          detail: { date: mondayCheckout, type: 'end' }
        }))
        
        // Update the selected range display
        this.updateSelectedRangeDisplay()
        return
      } else if (isFriday && isAdminOverrideEnabled) {
        console.log('*** FRIDAY DETECTED but ADMIN OVERRIDE ENABLED - allowing 1-night stay ***')
        
        // For admin override on Friday, allow next day selection
        const nextDay = new Date(startDate)
        nextDay.setDate(startDate.getDate() + 1)
        
        // Set minimum date to next day (allowing 1-night stay)
        this.endDatepicker.setOptions({
          minDate: nextDay
        })
        
        // Update the selected range display
        this.updateSelectedRangeDisplay()
        return
      } else if (isSaturday && isAdminOverrideEnabled) {
        console.log('*** SATURDAY DETECTED but ADMIN OVERRIDE ENABLED - allowing 1-night stay ***')
        
        // For admin override on Saturday, allow next day selection
        const nextDay = new Date(startDate)
        nextDay.setDate(startDate.getDate() + 1)
        
        // Set minimum date to next day (allowing 1-night stay)
        this.endDatepicker.setOptions({
          minDate: nextDay
        })
        
        // Update the selected range display
        this.updateSelectedRangeDisplay()
        return
      }
      
      // For non-weekend dates, continue with normal logic (but respect admin override)
      if (!isAdminOverrideEnabled) {
        // Get the appropriate minimum stay for this date
        const { minStay, stayType } = this.getMinimumStayForDate(startDate)
        
        // Calculate the suggested end date based on minimum stay
        const suggestedEndDate = new Date(startDate)
        suggestedEndDate.setDate(startDate.getDate() + minStay)
        
        // Always set the minimum date for the end datepicker
        this.endDatepicker.setOptions({
          minDate: suggestedEndDate
        })
        
        // For special dates (holidays, events), automatically set the end date
        if (stayType !== 'standard' && stayType !== 'weekend-friday' && stayType !== 'weekend-saturday') {
          console.log(`${stayType} check-in selected, automatically setting end date to ${this.formatDate(suggestedEndDate)} (${minStay}-night stay)`)
          
          // Update the end date input and datepicker
          this.updateDateInput(this.endInputTarget, this.formatDisplayDate(suggestedEndDate), this.formatDate(suggestedEndDate))
          this.endDatepicker.setDate(suggestedEndDate)
          
          // Dispatch a custom event for the end date change
          document.dispatchEvent(new CustomEvent('date-selected', {
            detail: { date: suggestedEndDate, type: 'end' }
          }))
        } else if (stayType === 'weekend-saturday') {
          // For Saturday, apply the weekend minimum stay
          console.log(`Saturday check-in selected, setting minimum stay to ${minStay} nights`)
          
          // If end date is before start date + minimum stay, update it
          const endDate = this.parseDate(this.endInputTarget.value)
          if (endDate && endDate < suggestedEndDate) {
            this.updateDateInput(this.endInputTarget, this.formatDisplayDate(suggestedEndDate), this.formatDate(suggestedEndDate))
            this.endDatepicker.setDate(suggestedEndDate)
          }
        } else {
          // For standard dates, just enforce the minimum stay if needed
          const endDate = this.parseDate(this.endInputTarget.value)
          if (endDate && endDate < suggestedEndDate) {
            this.updateDateInput(this.endInputTarget, this.formatDisplayDate(suggestedEndDate), this.formatDate(suggestedEndDate))
            this.endDatepicker.setDate(suggestedEndDate)
          }
        }
      } else {
        console.log('*** ADMIN OVERRIDE ENABLED - allowing flexible date selection ***')
        
        // For admin override, set minimum date to next day (allowing 1-night stays)
        const nextDay = new Date(startDate)
        nextDay.setDate(startDate.getDate() + 1)
        
        this.endDatepicker.setOptions({
          minDate: nextDay
        })
      }
      
      // Update the selected range display
      this.updateSelectedRangeDisplay()
      
      // Trigger auto-submit for availability filtering
      this.triggerAutoSubmit()
    }
  }
  
  handleEndDateChange(event) {
    // Check if this event was triggered by the date adjuster
    if (event.detail && event.detail.source === 'dateAdjuster' && event.detail.preventRecursion) {
      return
    }
    
    // Prevent handling if we're already processing a date change
    if (this.isProcessingDateChange) return
    
    try {
      // Set flag to prevent recursion
      this.isProcessingDateChange = true
      
      // Parse the selected end date
      const endDate = this.parseDate(event.target.value)
      if (!endDate) {
        this.isProcessingDateChange = false
        return
      }
      
      console.log('End date changed:', this.formatDate(endDate))
      
      // Update the end date input with formatted value for display
      this.updateDateInput(this.endInputTarget, this.formatDisplayDate(endDate), this.formatDate(endDate))
      
      // Dispatch a custom event for minimum stay validation
      document.dispatchEvent(new CustomEvent('date-selected', {
        detail: { date: endDate, type: 'end' }
      }))
      
      // Check minimum stay requirement if start date is set
      if (this.hasStartInputTarget) {
        const startDate = this.parseDate(this.startInputTarget.value)
        if (startDate) {
          // Calculate nights between start and end dates
          const nights = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24))
          
          // Check if minimum stay requirement is met
          if (nights < this.minStayValue) {
            // Dispatch minimum stay violation event
            this.dispatchMinimumStayViolation(nights)
          }
        }
      }
      
      // Update the selected range display
      this.updateSelectedRangeDisplay()
      
      // Trigger auto-submit for availability filtering
      this.triggerAutoSubmit()
      
      // Reset flag after a short delay
      setTimeout(() => {
        this.isProcessingDateChange = false
      }, 100)
    } catch (e) {
      console.error('Error handling end date change:', e)
      this.isProcessingDateChange = false
    }
  }
  
  dispatchMinimumStayViolation(currentStay) {
    // Don't dispatch if we're already processing a date change
    if (this.isProcessingDateChange) return
    
    // Dispatch custom event for minimum stay violation
    const event = new CustomEvent('minimumStayViolation', {
      bubbles: true,
      detail: {
        minimumStay: this.minStayValue,
        currentStay: currentStay
      }
    })
    
    this.element.dispatchEvent(event)
  }
  
  updateSelectedRangeDisplay() {
    if (!this.hasSelectedRangeTarget) return
    
    try {
      // Get start and end dates
      const startDate = this.hasStartInputTarget ? this.parseDate(this.startInputTarget.value) : null
      const endDate = this.hasEndInputTarget ? this.parseDate(this.endInputTarget.value) : null
      
      // Only update if both dates are valid
      if (startDate && endDate) {
        // Calculate nights between dates
        const nights = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24))
        
        // Format dates for display
        const formattedStartDate = this.formatDisplayDate(startDate)
        const formattedEndDate = this.formatDisplayDate(endDate)
        
        // Update display text
        const displayText = `${formattedStartDate} to ${formattedEndDate} (${nights} night${nights !== 1 ? 's' : ''})`
        
        const spanElement = this.selectedRangeTarget.querySelector('span')
        if (spanElement) {
          spanElement.textContent = displayText
        }
        
        // Show the selected range display
        this.selectedRangeTarget.classList.remove('hidden')
      } else {
        // Hide the selected range display if dates are invalid
        this.selectedRangeTarget.classList.add('hidden')
      }
    } catch (e) {
      console.error('Error updating selected range display:', e)
      // Hide the selected range display on error
      this.selectedRangeTarget.classList.add('hidden')
    }
  }
  
  parseDate(dateString) {
    if (!dateString) return null
    
    try {
      // Check if it's in YYYY-MM-DD format
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateString.split('-').map(Number)
        return new Date(year, month - 1, day)
      }
      
      // Check if it's in MM/DD/YYYY format
      if (dateString.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
        const [month, day, year] = dateString.split('/').map(Number)
        return new Date(year, month - 1, day)
      }
      
      // Try to parse as a date string
      const date = new Date(dateString)
      if (!isNaN(date.getTime())) {
        return date
      }
    } catch (e) {
      console.error('Error parsing date:', e)
    }
    
    return null
  }
  
  formatDisplayDate(date) {
    if (!date) return ''
    
    // Format date as MM/DD/YYYY for datepicker compatibility
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const year = date.getFullYear()
    
    return `${month}/${day}/${year}`
  }
  
  formatDate(date) {
    if (!date) return ''
    
    // Format date as YYYY-MM-DD
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    
    return `${year}-${month}-${day}`
  }
  
  updateDateInput(input, displayValue, backendValue) {
    if (!input) return
    
    // Set the visible value to the display format
    input.value = displayValue
    
    // Store the backend value as a data attribute for form submission
    input.dataset.backendValue = backendValue
  }
  
  beforeSubmit(event) {
    // Pathway 3: hide both popovers immediately on submit to avoid visual hangover
    this.startDatepicker?.hide()
    this.endDatepicker?.hide()

    // Format dates for backend processing before form submission
    if (this.hasStartInputTarget) {
      try {
        // Get the display value from the input
        const displayValue = this.startInputTarget.value
        
        // Parse the date and format it for the backend
        const startDate = this.parseDate(displayValue)
        if (startDate) {
          // Update the input value to the backend format for submission
          this.startInputTarget.value = this.formatDate(startDate)
        }
      } catch (e) {
        console.error('Error formatting start date before submit:', e)
      }
    }
    
    if (this.hasEndInputTarget) {
      try {
        // Get the display value from the input
        const displayValue = this.endInputTarget.value
        
        // Parse the date and format it for the backend
        const endDate = this.parseDate(displayValue)
        if (endDate) {
          // Update the input value to the backend format for submission
          this.endInputTarget.value = this.formatDate(endDate)
        }
      } catch (e) {
        console.error('Error formatting end date before submit:', e)
      }
    }
  }
  
  async initDatePickers() {
    console.log('Initializing date pickers...')
    
    // Lazy load Flowbite datepicker components
    const { Datepicker, DateRangePicker } = await window.loadFlowbiteDatepicker()
    
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    // Initialize start date picker
    if (this.hasStartInputTarget) {
      const startDateElement = this.startInputTarget
      
      try {
        // Check if we have a backend value stored in data attribute
        const hasBackendValue = startDateElement.dataset.backendValue && startDateElement.dataset.backendValue.trim() !== ''
        
        // If we have a visible value or backend value
        const hasValue = (startDateElement.value && startDateElement.value.trim() !== '') || hasBackendValue
        
        // Determine the default date - prefer visible value, then backend value, then today
        let defaultDate = null
        if (startDateElement.value && startDateElement.value.trim() !== '') {
          defaultDate = this.parseDate(startDateElement.value)
        } else if (hasBackendValue) {
          defaultDate = this.parseDate(startDateElement.dataset.backendValue)
        } else {
          defaultDate = today
        }
        
        // Create Flowbite datepicker for start date
        this.startDatepicker = new Datepicker(startDateElement, {
          format: 'mm/dd/yyyy', // Use MM/DD/YYYY format for better user experience
          minDate: this.allowPastDatesValue ? null : today,
          // Only set default date if we have a value and no placeholder
          // or if the placeholder is empty and we have a value
          defaultDate: (hasValue && !startDateElement.placeholder) ||
                      (startDateElement.placeholder === '' && hasValue) ?
                      defaultDate : undefined,
          autohide: true,
          todayBtn: true,
          clearBtn: false,
          todayBtnMode: 1,
          showOnFocus: true,
          disableTouchKeyboard: false,
          // Custom date filter to disable past dates
          datesDisabled: this.allowPastDatesValue ? [] : this.generatePastDates(today)
        })

        // Pathway 1 (start side): hide the end popover when the start input gains focus.
        // Note: on admin/failed_payments this controller runs as a single-input instance,
        // so endDatepicker will be undefined there — the ?. guard is intentional.
        startDateElement.addEventListener('focus', () => this.endDatepicker?.hide())

        // Override the datepicker's change handler
        const originalChangeHandler = this.startDatepicker._options.changeDate
        this.startDatepicker._options.changeDate = async (event) => {
          console.log('DIRECT TEST: Datepicker changeDate event', event)
          
          // Call our handler first
          await this.handleStartDateChange(event)
          
          // Then call the original handler if it exists
          if (originalChangeHandler) {
            originalChangeHandler(event)
          }
        }
        
        // Add event listener for date selection
        startDateElement.addEventListener('changeDate', async (event) => {
          if (event && event.detail && event.detail.date) {
            let finalDate = event.detail.date
            console.log('Start date selected:', finalDate)
            
            // Format for display
            this.updateDateInput(startDateElement, this.formatDisplayDate(finalDate), this.formatDate(finalDate))
            
            // Update end date minimum and adjust end date if needed
            if (this.hasEndInputTarget && this.endDatepicker) {
              // Let the backend API handle all date adjustment logic through PricingRule model
              // This removes hardcoded Friday→Sunday logic in favor of proper Rails business logic
              console.log('Date selected - backend API will handle minimum stay requirements via PricingRule model')
              
              // For non-Friday dates, continue with normal logic
              // Get the appropriate minimum stay for this date
              const { minStay, stayType } = this.getMinimumStayForDate(finalDate)
              
              // Calculate the suggested end date based on minimum stay
              const suggestedEndDate = new Date(finalDate)
              suggestedEndDate.setDate(finalDate.getDate() + minStay)
              
              // Check if admin override checkbox is checked first
              const adminOverrideCheckbox = document.querySelector('#reservation_override_minimum_stay')
              const isAdminOverride = adminOverrideCheckbox && adminOverrideCheckbox.checked
              
              console.log('🔍 DATEPICKER ADMIN OVERRIDE DEBUG:')
              console.log('  - Checkbox element:', adminOverrideCheckbox)
              console.log('  - Checkbox checked:', adminOverrideCheckbox ? adminOverrideCheckbox.checked : 'N/A')
              console.log('  - isAdminOverride:', isAdminOverride)
              
              if (isAdminOverride) {
                // Admin override: allow any date after start date (1-night minimum)
                const nextDay = new Date(finalDate)
                nextDay.setDate(finalDate.getDate() + 1)
                this.endDatepicker.setOptions({
                  minDate: nextDay
                })
                console.log('Admin override enabled: allowing 1-night minimum')
              } else {
                // Get actual minimum stay from API
                await this.setMinimumDateFromAPI(finalDate)
              }
              
              // For special dates (holidays, events), automatically set the end date unless admin override
              if (stayType !== 'standard' && stayType !== 'weekend-friday' && stayType !== 'weekend-saturday' && !isAdminOverride) {
                console.log(`${stayType} check-in selected, automatically setting end date to ${this.formatDate(suggestedEndDate)} (${minStay}-night stay)`)
                
                // Update the end date input and datepicker
                this.updateDateInput(this.endInputTarget, this.formatDisplayDate(suggestedEndDate), this.formatDate(suggestedEndDate))
                this.endDatepicker.setDate(suggestedEndDate)
                
                // Dispatch a custom event for the end date change
                document.dispatchEvent(new CustomEvent('date-selected', {
                  detail: { date: suggestedEndDate, type: 'end' }
                }))
              } else if (stayType !== 'standard' && stayType !== 'weekend-friday' && stayType !== 'weekend-saturday' && isAdminOverride) {
                console.log(`${stayType} check-in selected, but admin override enabled - allowing manual end date selection`)
              } else if (stayType === 'weekend-saturday') {
                // Check if admin override is enabled
                const adminOverrideCheckbox = document.querySelector('#reservation_override_minimum_stay')
                const isAdminOverrideEnabled = adminOverrideCheckbox && adminOverrideCheckbox.checked
                
                console.log('🔍 ADMIN OVERRIDE DEBUG (startDateChanged):')
                console.log('  - Checkbox found:', !!adminOverrideCheckbox)
                console.log('  - Checkbox checked:', adminOverrideCheckbox ? adminOverrideCheckbox.checked : 'N/A')
                console.log('  - Override enabled:', isAdminOverrideEnabled)
                
                if (isAdminOverrideEnabled) {
                  console.log('*** SATURDAY DETECTED but ADMIN OVERRIDE ENABLED - allowing 1-night stay ***')
                  // Allow 1-night stays when admin override is enabled
                  const oneDayLater = new Date(finalDate)
                  oneDayLater.setDate(finalDate.getDate() + 1)
                  
                  // Set minimum date to allow 1-night stay
                  this.endDatepicker.setOptions({
                    minDate: oneDayLater
                  })
                } else {
                  // For Saturday, apply the weekend minimum stay
                  console.log(`Saturday check-in selected, setting minimum stay to ${minStay} nights`)
                  
                  // If end date is before start date + minimum stay, update it
                  const endDate = this.parseDate(this.endInputTarget.value)
                  if (endDate && endDate < suggestedEndDate) {
                    this.updateDateInput(this.endInputTarget, this.formatDisplayDate(suggestedEndDate), this.formatDate(suggestedEndDate))
                    this.endDatepicker.setDate(suggestedEndDate)
                  }
                }
              } else {
                // For standard dates, just enforce the minimum stay if needed
                const endDate = this.parseDate(this.endInputTarget.value)
                if (endDate && endDate < suggestedEndDate) {
                  this.updateDateInput(this.endInputTarget, this.formatDisplayDate(suggestedEndDate), this.formatDate(suggestedEndDate))
                  this.endDatepicker.setDate(suggestedEndDate)
                }
              }
              
              // Update the selected range display
              this.updateSelectedRangeDisplay()
            }
            
            // Update start date hidden field
            if (this.hasStartHiddenTarget) {
              this.startHiddenTarget.value = this.formatDate(finalDate)
            }
            
            // Dispatch a custom event to notify other controllers
            this.dispatch('dateChanged', { 
              type: 'start',
              date: finalDate
            })
          }
        })
        
        // If we have a placeholder and no value, ensure the input is empty
        if (startDateElement.placeholder && !startDateElement.value) {
          startDateElement.value = ''
        }
        
        console.log('Start date picker initialized successfully')
      } catch (e) {
        console.error('Error initializing start date picker:', e)
      }
    }
    
    // Initialize end date picker
    if (this.hasEndInputTarget) {
      const endDateElement = this.endInputTarget
      
      try {
        // Check if we have a backend value stored in data attribute
        const hasBackendValue = endDateElement.dataset.backendValue && endDateElement.dataset.backendValue.trim() !== ''
        
        // If we have a visible value or backend value
        const hasValue = (endDateElement.value && endDateElement.value.trim() !== '') || hasBackendValue
        
        // Get the start date if it exists
        let startDate = null
        if (this.hasStartInputTarget) {
          startDate = this.parseDate(this.startInputTarget.value)
        }
        
        // Calculate minimum end date (either day after start date or tomorrow)
        let minEndDate = new Date(today)
        minEndDate.setDate(minEndDate.getDate() + 1)
        
        if (startDate) {
          const dayAfterStart = new Date(startDate)
          dayAfterStart.setDate(dayAfterStart.getDate() + 1)
          if (dayAfterStart > minEndDate) {
            minEndDate = dayAfterStart
          }
        }
        
        // Determine the default date - prefer visible value, then backend value, then minimum end date
        let defaultDate = null
        if (endDateElement.value && endDateElement.value.trim() !== '') {
          defaultDate = this.parseDate(endDateElement.value)
          // Ensure default date is not before minimum
          if (defaultDate < minEndDate) {
            defaultDate = minEndDate
          }
        } else if (hasBackendValue) {
          defaultDate = this.parseDate(endDateElement.dataset.backendValue)
          // Ensure default date is not before minimum
          if (defaultDate < minEndDate) {
            defaultDate = minEndDate
          }
        } else {
          defaultDate = minEndDate
        }
        
        // Create Flowbite datepicker for end date
        this.endDatepicker = new Datepicker(endDateElement, {
          format: 'mm/dd/yyyy', // Use MM/DD/YYYY format for better user experience
          minDate: this.allowPastDatesValue ? null : minEndDate,
          // Only set default date if we have a value and no placeholder
          // or if the placeholder is empty and we have a value
          defaultDate: (hasValue && !endDateElement.placeholder) ||
                      (endDateElement.placeholder === '' && hasValue) ?
                      defaultDate : undefined,
          autohide: true,
          todayBtn: true,
          clearBtn: false,
          todayBtnMode: 1,
          showOnFocus: true,
          disableTouchKeyboard: false,
          // Custom date filter to disable past dates
          datesDisabled: this.allowPastDatesValue ? [] : this.generatePastDates(minEndDate)
        })

        // Pathway 1 (end side): hide the start popover when the end input gains focus.
        endDateElement.addEventListener('focus', () => this.startDatepicker?.hide())

        // Add event listener for date selection
        endDateElement.addEventListener('changeDate', (event) => {
          if (event && event.detail && event.detail.date) {
            let finalDate = event.detail.date
            console.log('End date selected:', finalDate)
            
            // Get start date
            const startDate = this.hasStartInputTarget ? 
              this.parseDate(this.startInputTarget.value) : 
              new Date()
            
            // Ensure end date is not before start date + 1
            const minEndDate = new Date(startDate)
            minEndDate.setDate(minEndDate.getDate() + 1)
            
            if (finalDate < minEndDate) {
              console.log('Selected end date is before minimum, adjusting to:', minEndDate)
              finalDate = minEndDate
              this.updateDateInput(this.endInputTarget, this.formatDisplayDate(minEndDate), this.formatDate(minEndDate))
              this.endDatepicker.setDate(minEndDate)
            } else {
              // Format for display
              this.updateDateInput(this.endInputTarget, this.formatDisplayDate(finalDate), this.formatDate(finalDate))
            }
            
            // Update hidden field
            if (this.hasEndHiddenTarget) {
              this.endHiddenTarget.value = this.formatDate(finalDate)
            }
            
            // Calculate nights
            const nights = Math.round((finalDate - startDate) / (1000 * 60 * 60 * 24))
            console.log('Selected stay duration:', nights, 'nights')
            
            // Dispatch a custom event to notify other controllers
            this.dispatch('dateChanged', { 
              type: 'end',
              date: finalDate,
              nights: nights
            })
          }
        })
        
        // If we have a placeholder and no value, ensure the input is empty
        if (endDateElement.placeholder && !endDateElement.value) {
          endDateElement.value = ''
        }
        
        console.log('End date picker initialized successfully')
      } catch (e) {
        console.error('Error initializing end date picker:', e)
      }
    }
    
    // Initialize date range picker if both inputs exist
    if (this.hasStartInputTarget && this.hasEndInputTarget && this.hasStartDateTarget && this.hasEndDateTarget) {
      try {
        // We'll use individual pickers instead of DateRangePicker for better control
        console.log('Using individual date pickers for date range selection')
      } catch (e) {
        console.error('Error with date range picker setup:', e)
      }
    }
  }
  
  generatePastDates(date) {
    const pastDates = []
    const today = new Date(date)
    
    for (let i = 1; i <= 365; i++) {
      const pastDate = new Date(today)
      pastDate.setDate(pastDate.getDate() - i)
      pastDates.push(pastDate)
    }
    
    return pastDates
  }
  
  getMinimumStayForDate(date) {
    if (!date) return { minStay: this.minStayValue || 1, stayType: 'standard' }
    
    // Format the date for comparison with holiday dates
    const formattedDate = this.formatDate(date)
    
    // Check if it's a holiday or event date
    // We need to fetch this information from the server or data attributes
    const holidayMinStay = this.holidayMinimumStayValue
    
    // Check if we have holiday data from the minimum-stay-validator controller
    const minimumStayValidator = document.querySelector('[data-controller~="minimum-stay-validator"]')
    let isHolidayOrEvent = false
    let holidayName = ''
    
    if (minimumStayValidator) {
      // Check if we have holiday dates from the validator
      const holidayDates = minimumStayValidator.dataset.minimumStayValidatorHolidayDatesValue
      if (holidayDates) {
        try {
          const dates = JSON.parse(holidayDates)
          isHolidayOrEvent = dates.some(holiday => {
            const isInRange = this.isDateInRange(date, holiday.start_date, holiday.end_date)
            if (isInRange) {
              holidayName = holiday.name
            }
            return isInRange
          })
        } catch (e) {
          console.error('Error parsing holiday dates:', e)
        }
      }
    }
    
    if (isHolidayOrEvent && holidayMinStay) {
      return { 
        minStay: holidayMinStay, 
        stayType: 'holiday',
        name: holidayName
      }
    }
    
    // Check if it's a weekend day (Friday or Saturday)
    const day = date.getDay()
    const isFriday = day === 5
    const isSaturday = day === 6
    
    if (isFriday && this.weekendMinimumStayValue) {
      return { 
        minStay: this.weekendMinimumStayValue, 
        stayType: 'weekend-friday' 
      }
    }
    
    if (isSaturday && this.weekendMinimumStayValue) {
      // For Saturday, we might have a different rule based on booking pattern
      // For now, we'll return the weekend minimum stay but with a different type
      return { 
        minStay: this.weekendMinimumStayValue, 
        stayType: 'weekend-saturday' 
      }
    }
    
    // Default to base minimum stay
    return { 
      minStay: this.minStayValue || 1, 
      stayType: 'standard' 
    }
  }
  
  // Helper to check if a date is within a range
  isDateInRange(date, startDateStr, endDateStr) {
    if (!date || !startDateStr || !endDateStr) return false
    
    try {
      const startDate = this.parseDate(startDateStr)
      const endDate = this.parseDate(endDateStr)
      
      if (!startDate || !endDate) return false
      
      // Reset time components for accurate date comparison
      const compareDate = new Date(date)
      compareDate.setHours(0, 0, 0, 0)
      startDate.setHours(0, 0, 0, 0)
      endDate.setHours(0, 0, 0, 0)
      
      return compareDate >= startDate && compareDate <= endDate
    } catch (e) {
      console.error('Error comparing dates:', e)
      return false
    }
  }

  // Get minimum stay from API and set datepicker restrictions
  async setMinimumDateFromAPI(startDate) {
    try {
      // Get the selected rental ID
      const rentalSelect = document.querySelector('#reservation_rental_id')
      if (!rentalSelect || !rentalSelect.value) {
        console.log('No rental selected, using default 1-night minimum')
        const nextDay = new Date(startDate)
        nextDay.setDate(startDate.getDate() + 1)
        this.endDatepicker.setOptions({ minDate: nextDay })
        return
      }

      const response = await fetch('/admin/rentals/minimum_stay_check', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          rental_id: rentalSelect.value,
          start_date: this.formatDate(startDate),
          end_date: this.formatDate(new Date(startDate.getTime() + 24 * 60 * 60 * 1000))
        })
      })

      if (response.ok) {
        const data = await response.json()
        const minimumStay = data.minimum_stay_required || 1
        
        // Calculate minimum end date based on API response
        const suggestedEndDate = new Date(startDate)
        suggestedEndDate.setDate(startDate.getDate() + minimumStay)
        
        this.endDatepicker.setOptions({ minDate: suggestedEndDate })
        console.log(`API: Setting minimum end date to ${this.formatDate(suggestedEndDate)} (${minimumStay}-night minimum for ${data.rental_name})`)
      } else {
        console.error('Failed to get minimum stay from API')
        // Fallback to 1-night minimum
        const nextDay = new Date(startDate)
        nextDay.setDate(startDate.getDate() + 1)
        this.endDatepicker.setOptions({ minDate: nextDay })
      }
    } catch (error) {
      console.error('Error calling minimum stay API:', error)
      // Fallback to 1-night minimum
      const nextDay = new Date(startDate)
      nextDay.setDate(startDate.getDate() + 1)
      this.endDatepicker.setOptions({ minDate: nextDay })
    }
  }
  
  // Trigger auto-submit controller when dates change
  triggerAutoSubmit() {
    // Find the auto-submit controller on the same element
    const autoSubmitController = this.application.getControllerForElementAndIdentifier(this.element, 'auto-submit')
    if (autoSubmitController && autoSubmitController.submit) {
      console.log('🚀 Triggering auto-submit from datepicker')
      autoSubmitController.submit()
    } else {
      console.warn('Auto-submit controller not found or submit method not available')
    }
  }
}
